import React, { useState } from 'react';
import { Terminal, Copy, Check, Info, ShieldAlert } from 'lucide-react';
import { toast } from 'react-toastify';

const EmbedCodeBlock = ({ websiteConfig, onRegenerateApiKey, isProcessing }) => {
    const [copied, setCopied] = useState(false);

    if (!websiteConfig?.domain || !websiteConfig?.apiKey) {
        return null;
    }

    // Points to the widget.js hosted on the backend API server (not this React app)
    const WIDGET_URL = `${process.env.REACT_APP_NODE_BASE_URL}/widget.js`;

    const embedCode = `<script src="${WIDGET_URL}" data-api-key="${websiteConfig.apiKey}"></script>`;

    const handleCopy = () => {
        navigator.clipboard.writeText(embedCode);
        setCopied(true);
        toast.success("Embed code copied to clipboard!");

        setTimeout(() => {
            setCopied(false);
        }, 3000);
    };

    return (
        <div className="bg-white rounded-xl border border-[#E0E4E2] shadow-sm overflow-hidden mt-6">
            <div className="p-6 border-b border-[#E0E4E2] bg-[#FDFDFD] flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Terminal className="w-5 h-5 text-[#1E6C56]" />
                        Installation Code
                    </h3>
                    <p className="text-gray-500 text-sm mt-1">
                        Paste this code snippet right before the closing <code className="bg-gray-100 px-1 rounded">&lt;/head&gt;</code> tag on {websiteConfig.domain}.
                    </p>
                </div>

                {/* Warning about API key safety */}
                <div className="hidden sm:flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200">
                    <ShieldAlert className="w-4 h-4" />
                    Keep your API key secure
                </div>
            </div>

            <div className="p-6">
                <div className="relative group">
                    <div className="absolute inset-y-0 left-0 bg-gray-900 w-1 rounded-l-xl"></div>
                    <pre className="bg-gray-900 text-gray-100 p-5 pl-8 rounded-xl overflow-x-auto text-sm font-mono shadow-inner">
                        <code>{embedCode}</code>
                    </pre>

                    <button
                        onClick={handleCopy}
                        className={`absolute top-1/2 -translate-y-1/2 right-4 p-2.5 rounded-lg font-medium transition-all shadow-sm ${copied
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm'
                            }`}
                        title="Copy to clipboard"
                    >
                        {copied ? (
                            <Check className="w-5 h-5" />
                        ) : (
                            <Copy className="w-5 h-5" />
                        )}
                    </button>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                    <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                        <div>
                            <h4 className="text-sm font-bold text-gray-900">Need a new API key?</h4>
                            <p className="text-xs text-gray-600 mt-1 max-w-lg">
                                If you suspect your API key has been compromised, you can regenerate it.
                                <strong className="text-red-600 font-semibold block mt-1">Warning: Regenerating will instantly break the translation widget on your site until you update the code.</strong>
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={onRegenerateApiKey}
                        disabled={isProcessing}
                        className="shrink-0 px-4 py-2 border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg text-sm font-bold transition-colors disabled:opacity-50"
                    >
                        {isProcessing ? 'Processing...' : 'Regenerate API Key'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmbedCodeBlock;
